import { getBearerToken } from "../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import type { SupportTicketPageApiResponse } from "../supportTicketTypes";

/** Default page size for the support tickets sidebar list. */
export const SUPPORT_TICKETS_DEFAULT_PAGE_SIZE = 200;

/**
 * Request body for POST /pharmacy/support-tickets/get-tickets.
 */
export interface FetchSupportTicketsParams {
  org_id: string;
  /** True returns only archived tickets; false (default) returns active tickets. */
  is_archived?: boolean;
  page?: number;
  limit?: number;
}

/**
 * Fetches paginated support tickets for the current organization.
 *
 * @param params - Organization id, optional archive filter, and pagination values.
 * @returns Paginated support ticket rows ordered by most recently updated.
 */
export const fetchSupportTickets = async (
  params: FetchSupportTicketsParams,
): Promise<SupportTicketPageApiResponse> => {
  const body = {
    org_id: params.org_id,
    is_archived: params.is_archived ?? false,
    page: params.page ?? 1,
    limit: params.limit ?? SUPPORT_TICKETS_DEFAULT_PAGE_SIZE,
  };

  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.GET_SUPPORT_TICKETS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const result = (await response.json()) as SupportTicketPageApiResponse;
    logDataToConsole("fetchSupportTickets", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in fetchSupportTickets");
    throw error;
  }
};
